import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import LoadingOverlay from 'react-loading-overlay'
import PropagateLoader from 'react-spinners/PropagateLoader'

import { useModuleContext } from '../../contexts/ModuleContext'
import { useTroubleshoot } from '../../contexts/TroubleshootContext'
import * as Solver from '../../Solver'
import { FaultAttempt } from '../../Types'

import NavigationBar from '../NavigationBar'
import { FaultAttemptDetails } from './FaultAttemptDetails'
import { isDebug } from '../../App.config'
import { storeInSession, getFromSession } from '../../Utils'

import './PostAssessment.scss'
import { SecondaryButton, ButtonGroup } from '../../styles/Buttons'
import { moduleContent } from '../../Module.config'


const PostAssessment: React.FC = () => {
    const sessionFaultAttempt = useMemo(() => getFromSession('faultAttempt'), [])
    const { connection, groupName, solverAvailable } = useModuleContext()
    const { state } = useTroubleshoot()
    const [solverCallbackLoaded, setSolverCallbackLoaded] = useState(false)
    const [faultAttempt, setFaultAttempt] = useState<FaultAttempt>(
        () => !sessionFaultAttempt
            ? {} as FaultAttempt
            : JSON.parse(sessionFaultAttempt) as FaultAttempt
    )

    const history = useHistory()

    const faultStateUpdate = useCallback((data: string) => {
        isDebug && console.log(`%cSolver sent Fault State Update.`, Solver.solverCSS)
        storeInSession('faultAttempt', data)
        const faultAttempt = JSON.parse(data) as FaultAttempt
        setFaultAttempt(faultAttempt)
        isDebug && console.log('Fault Attempt saved.', faultAttempt)
    }, [])

    const registerSolverCallback = useCallback(() => {
        if (solverCallbackLoaded) return
        isDebug && console.log('Registering Post Assessment solver callback...')
        connection.on('clientFaultStateUpdate', faultStateUpdate)
        setSolverCallbackLoaded(true)
    }, [connection, faultStateUpdate, solverCallbackLoaded])

    const unregisterSolverCallback = useCallback(() => {
        if (!solverCallbackLoaded) return
        isDebug && console.log('UNREGISTER Post Assessment solver callback...')
        connection.off('clientFaultStateUpdate', faultStateUpdate)
    }, [connection, faultStateUpdate, solverCallbackLoaded])

    useEffect(() => {
        if (!solverCallbackLoaded) return

        if (state.faultId === undefined && !sessionFaultAttempt) {
            console.error('Fault ID is missing. Cannot send completeFault to solver.')
            history.push('/scoreboard')
            return
        }
        if (sessionFaultAttempt) return // We already sent completeFault and received a FaultAttempt.

        isDebug && console.log('Post assessment handling CompleteFault...')
        const completeFault = async () => await Solver.completeFault(state.faultId!, connection, groupName)
        completeFault()
    }, [connection, groupName, history, sessionFaultAttempt, solverCallbackLoaded, state.faultId])


    useEffect(() => {
        if (!solverCallbackLoaded) registerSolverCallback()
        return () => unregisterSolverCallback()
    }, [registerSolverCallback, solverCallbackLoaded, unregisterSolverCallback])

    const gotoScoreboard = (event: any) => {
        event.preventDefault()
        history.push('/scoreboard')
    }

    const notReady = !faultAttempt.performanceSummary || !solverAvailable

    const TopBar = () => <NavigationBar pageTitle={moduleContent.title} />

    if (notReady) return (
        <div className="body-override">
            <TopBar />
            <Container className="post-fault" fluid={true}>
                <LoadingOverlay
                    active={notReady}
                    spinner={<PropagateLoader color="#f47e56" />}
                    classNamePrefix="loader-"
                    text={`Loading your fault results`}
                />
            </Container>
        </div>
    )

    return (
        <div className="body-override">
            <TopBar />
            <FaultAttemptDetails {...faultAttempt} />
            <Container>
                <Row>
                    <Col>
                        <ButtonGroup>
                            {/*<button className="button primary">Show Best Practice</button>*/}
                            <SecondaryButton onClick={gotoScoreboard}>
                                {'OK'}
                            </SecondaryButton>
                        </ButtonGroup>
                    </Col>

                </Row>
            </Container>
        </div>
    )
}

export default PostAssessment